import type { EventsKey } from 'ol/events';
import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import { unByKey } from 'ol/Observable';
import type { AppStore } from '../core/store';
import type { AppState, MapTool } from '../core/types';
import { formatDistance } from '../domain/measurement';
import { isPlanningLayer } from '../domain/planningObjects';
import { elevationAtMapCoordinate, formatMetres } from '../elevation/elevationQuery';
import type { DemSource } from '../elevation/demSource';
import { buildProfile, type ElevationProfile } from '../elevation/profile';
import { MAX_PROFILE_SAMPLES, MAX_PROFILE_SPACING_M, MIN_PROFILE_SPACING_M, parseProfileSpacing, PROFILE_SPACING_M, PROFILE_SPACING_OPTIONS, sampleRoute, type SampledRoute } from '../elevation/routeSampling';
import type { DrawingController } from '../map/DrawingController';
import type { ElevationOverlay } from '../map/ElevationOverlay';
import { ElevationProfileChart } from './ElevationProfileChart';
import { ELEVATION_DISCLAIMER, HYDRAULIC_CAVEAT } from './ElevationSourceInfo';
import { icon } from './icons';

const esc = (value: string): string => value.replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char] ?? char);

/** Where the profiled line came from. A planning line is read, never written. */
type ProfileSource =
  | { readonly kind: 'drawn' }
  | { readonly kind: 'planning'; readonly layerId: string; readonly featureId: string; readonly name: string; readonly geometry: Geometry; readonly revision: number };

/** A planning line in the current selection, if any (read-only source for a profile). */
export function selectedPlanningLine(state: AppState): { readonly layerId: string; readonly featureId: string; readonly name: string; readonly geometry: LineString } | null {
  const selection = state.genericSelection; if (!selection) return null;
  const layer = state.layers.find(({ id }) => id === selection.layerId);
  if (!isPlanningLayer(layer)) return null;
  const record = layer.featuresById.get(selection.featureId);
  const geometry = record?.feature.getGeometry();
  if (!record || !(geometry instanceof LineString) || geometry.getCoordinates().length < 2) return null;
  const name = String(record.attributes.Nombre ?? record.attributes.Tipo ?? 'Línea de planificación');
  return { layerId: layer.id, featureId: record.id, name, geometry };
}

/** Per-browser preference for «Cotas cada» (never stored in the project or the .casurgeo). */
export const PROFILE_SPACING_STORAGE_KEY = 'casur-geostudio.elevation.profileSpacing';
const readStoredSpacing = (): number => { try { return parseProfileSpacing(window.localStorage.getItem(PROFILE_SPACING_STORAGE_KEY) ?? '') ?? PROFILE_SPACING_M; } catch { return PROFILE_SPACING_M; } };
const storeSpacing = (value: number): void => { try { window.localStorage.setItem(PROFILE_SPACING_STORAGE_KEY, String(value)); } catch { /* private mode: keep it for this session only */ } };
const metresLabel = (value: number): string => `${value.toLocaleString('es-NI', { maximumFractionDigits: 1 })} m`;

const fmt = (value: number | null | undefined, signed = false): string => {
  if (value === null || value === undefined) return 'Sin dato';
  const text = formatMetres(Math.abs(value));
  return signed ? `${value > 0 ? '+' : value < 0 ? '−' : ''}${text}` : formatMetres(value);
};

/**
 * «Perfil de elevación»: compact bottom panel over the map. The line is either drawn temporarily or taken (read-only)
 * from a selected planning line. Nothing is added to the project, the Maestro or the .casurgeo; closing the panel
 * removes the drawing interaction and every temporary graphic.
 */
export class ElevationProfilePanel {
  private open = false;
  private drawing = false;
  /** Set while this panel cancels its own capture, so the capture's cancel callback does not close the panel. */
  private cancelling = false;
  private previousTool: MapTool | null = null;
  private source: ProfileSource | null = null;
  private coordinates: (readonly number[])[] = [];
  private profile: ElevationProfile | null = null;
  private stale = false;
  private computeToken = 0;
  private geometryListener: EventsKey | null = null;
  private chart: ElevationProfileChart | null = null;
  private spacing = readStoredSpacing();
  private route: SampledRoute | null = null;

  constructor(
    private readonly element: HTMLElement,
    private readonly store: AppStore,
    private readonly dem: DemSource,
    private readonly drawingController: DrawingController,
    private readonly overlay: ElevationOverlay,
    private readonly openSource: () => void,
    private readonly beforeDraw: () => void = () => undefined,
  ) {
    element.addEventListener('click', (event) => {
      const action = (event.target as Element).closest<HTMLElement>('[data-profile-action]')?.dataset.profileAction;
      if (action === 'close') this.close();
      else if (action === 'draw') this.startDrawing();
      else if (action === 'selected') this.useSelectedLine();
      else if (action === 'recalculate') this.recalculate();
      else if (action === 'source') this.openSource();
    });
    element.addEventListener('change', (event) => {
      const target = event.target as HTMLElement;
      if (target.matches('[data-profile-spacing]')) this.onSpacingPreset((target as HTMLSelectElement).value);
      else if (target.matches('[data-profile-spacing-custom]')) this.onSpacingCustom((target as HTMLInputElement).value);
    });
    element.addEventListener('keydown', (event) => {
      const target = event.target as HTMLElement;
      if (event.key === 'Enter' && target.matches('[data-profile-spacing-custom]')) { event.preventDefault(); this.onSpacingCustom((target as HTMLInputElement).value); }
    });
    store.subscribe((state, previous) => {
      if (!this.open) return;
      if (state.layers !== previous.layers) this.checkStale(state);
      if (state.genericSelection !== previous.genericSelection || state.layers !== previous.layers) this.syncSelectedButton(state);
    });
    dem.onChange(() => { if (this.open && this.coordinates.length >= 2 && !this.profile) this.compute(); });
  }

  get isOpen(): boolean { return this.open; }
  get isDrawing(): boolean { return this.drawing; }
  get isStale(): boolean { return this.stale; }
  get currentProfile(): ElevationProfile | null { return this.profile; }
  get currentRoute(): SampledRoute | null { return this.route; }
  get samplingInterval(): number { return this.spacing; }

  /**
   * Changes «Cotas cada» (m). Validated; recomputes the current profile from the same line (the geometry is never
   * touched). Returns false for an invalid distance. A stale profile keeps waiting for «Recalcular».
   */
  setSamplingInterval(value: number): boolean {
    const spacing = parseProfileSpacing(value);
    if (spacing === null) return false;
    this.spacing = spacing; storeSpacing(spacing);
    this.syncSpacingControl();
    if (this.stale) this.setMessage(`Intervalo ${metresLabel(spacing)} guardado; se aplicará al pulsar «Recalcular».`);
    else if (this.coordinates.length >= 2 && !this.drawing) this.compute();
    return true;
  }

  toggle(): void { if (this.open) this.close(); else this.openPanel(); }

  /** Opens the panel: profiles the selected planning line, or starts drawing a temporary line. */
  openPanel(): void {
    this.open = true;
    this.element.hidden = false;
    this.element.parentElement?.classList.add('elevation-profile-open');
    void this.dem.load();
    this.renderShell();
    this.syncButtons();
    if (selectedPlanningLine(this.store.getState())) this.useSelectedLine(); else this.startDrawing();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.cancelDrawing();
    this.detachGeometry();
    this.computeToken += 1;
    this.chart?.dispose(); this.chart = null;
    this.overlay.clearRoute();
    this.source = null; this.coordinates = []; this.profile = null; this.route = null; this.stale = false;
    this.element.hidden = true; this.element.innerHTML = '';
    this.element.parentElement?.classList.remove('elevation-profile-open');
    this.syncButtons();
  }

  startDrawing(): void {
    if (!this.open) return;
    this.cancelDrawing();
    this.beforeDraw();
    const tool = this.store.getState().activeTool;
    this.previousTool = tool === 'navigate' ? 'select' : tool;
    // The tool goes first: changing it resets map interactions, the capture is added afterwards.
    if (tool !== 'navigate') this.store.update({ activeTool: 'navigate' }, { markDirty: false });
    this.drawing = true;
    this.setMessage('Dibuja la línea del perfil: clic en cada vértice, doble clic para terminar · Esc cancela.');
    this.drawingController.captureGeometry('LineString', (geometry) => {
      this.drawing = false; this.restoreTool();
      if (geometry instanceof LineString) this.setRoute(geometry.getCoordinates(), { kind: 'drawn' });
    }, {
      label: 'línea de perfil',
      cancel: () => {
        if (this.cancelling) return;
        this.drawing = false; this.restoreTool();
        if (!this.profile) this.close(); else this.setMessage('');
      },
    });
    if (window.matchMedia?.('(max-width: 760px)').matches) document.querySelector<HTMLElement>('.app-shell[data-mobile-dock] [data-mobile-close]')?.click();
  }

  useSelectedLine(): void {
    const line = selectedPlanningLine(this.store.getState());
    if (!line) { this.setMessage('Selecciona una tubería o línea de planificación en el mapa.'); return; }
    this.cancelDrawing();
    this.setRoute(line.geometry.getCoordinates(), { kind: 'planning', layerId: line.layerId, featureId: line.featureId, name: line.name, geometry: line.geometry, revision: line.geometry.getRevision() });
  }

  /** Recomputes from the current geometry of the planning line (after it was edited). */
  recalculate(): void {
    const source = this.source;
    if (source?.kind !== 'planning') { if (this.coordinates.length >= 2) this.compute(); return; }
    const layer = this.store.getState().layers.find(({ id }) => id === source.layerId);
    const geometry = layer?.featuresById.get(source.featureId)?.feature.getGeometry();
    if (!(geometry instanceof LineString)) { this.setMessage('La línea ya no existe: dibuja una nueva o selecciona otra.'); return; }
    this.setRoute(geometry.getCoordinates(), { ...source, geometry, revision: geometry.getRevision() });
  }

  /** Chart hover → map marker (also used by tests). */
  highlight(index: number | null): void { this.chart?.highlight(index); }

  private setRoute(coordinates: (readonly number[])[], source: ProfileSource): void {
    this.detachGeometry();
    this.source = source; this.coordinates = coordinates.map((coordinate) => [coordinate[0]!, coordinate[1]!]); this.stale = false; this.profile = null; this.route = null;
    if (source.kind === 'planning') this.geometryListener = source.geometry.on('change', () => this.markStale('La línea cambió: el perfil está desactualizado.'));
    this.overlay.showRoute(this.coordinates);
    this.compute();
  }

  private compute(): void {
    const token = ++this.computeToken;
    if (this.dem.status !== 'ready') {
      this.renderStats(null);
      this.setMessage(this.dem.status === 'error' ? `Modelo de elevación no disponible: ${this.dem.error ?? ''}` : 'Cargando modelo de elevación…');
      if (this.dem.status !== 'error') void this.dem.load().then(() => { if (token === this.computeToken && this.open) this.compute(); });
      return;
    }
    const route = sampleRoute(this.coordinates, this.spacing);
    this.route = route;
    this.profile = buildProfile(route, (sample) => elevationAtMapCoordinate(this.dem, sample.coordinate));
    this.stale = false;
    this.overlay.setStale(false);
    this.element.querySelector<HTMLElement>('[data-profile-action="recalculate"]')?.setAttribute('hidden', '');
    this.renderStats(this.profile);
    this.renderSampling();
    this.chart?.render(this.profile);
    const { stats } = this.profile;
    const messages = [
      route.decimated ? `La línea tiene ${route.vertexCount.toLocaleString('es-NI')} vértices: el perfil lee ${route.samples.length.toLocaleString('es-NI')} de ellos, repartidos a lo largo del recorrido (límite de ${MAX_PROFILE_SAMPLES.toLocaleString('es-NI')} lecturas; la tubería no se modifica).`
        : route.limited ? `Límite de ${MAX_PROFILE_SAMPLES.toLocaleString('es-NI')} lecturas: para esta longitud el paso real es de ${metresLabel(route.spacing)}, mayor que los ${metresLabel(route.requestedSpacing)} pedidos.` : '',
      stats.validSamples === 0 ? 'Sin datos de elevación en esta línea (fuera de cobertura o sobre agua).' : stats.coverage < 0.999 ? `Cobertura parcial: ${Math.round(stats.coverage * 100)} % de la longitud tiene datos; los tramos sin dato no se unen.` : '',
    ].filter(Boolean);
    this.setMessage(messages.join(' '));
  }

  private checkStale(state: AppState): void {
    const source = this.source; if (source?.kind !== 'planning' || this.stale) return;
    const layer = state.layers.find(({ id }) => id === source.layerId);
    const geometry = layer?.featuresById.get(source.featureId)?.feature.getGeometry();
    if (!geometry) this.markStale('La línea fue eliminada: el perfil ya no corresponde a ningún objeto.');
    else if (geometry !== source.geometry || geometry.getRevision() !== source.revision) this.markStale('La línea cambió: el perfil está desactualizado.');
  }

  private markStale(message: string): void {
    if (this.stale) return;
    this.stale = true;
    this.overlay.setStale(true);
    this.element.classList.add('stale');
    this.element.querySelector<HTMLElement>('[data-profile-action="recalculate"]')?.removeAttribute('hidden');
    this.setMessage(`${message} Pulsa «Recalcular».`);
  }

  private renderShell(): void {
    this.element.classList.remove('stale');
    this.element.innerHTML = `
      <header class="elevation-profile-header">
        <strong>${icon('profile')}Perfil de elevación</strong>
        <span class="elevation-profile-source" data-profile-name></span>
        <span class="elevation-profile-actions">
          <button type="button" class="button" data-profile-action="recalculate" hidden>Recalcular</button>
          <button type="button" class="button" data-profile-action="draw">${icon('line')}Dibujar línea</button>
          <button type="button" class="button" data-profile-action="selected" title="Usar la tubería o línea de planificación seleccionada (solo lectura)">${icon('line')}Usar selección</button>
          <button type="button" class="status-source" data-profile-action="source" title="Fuente y limitaciones de la cota">GLO‑30</button>
          <button type="button" class="ghost-icon" data-profile-action="close" aria-label="Cerrar perfil" title="Cerrar perfil">${icon('close')}</button>
        </span>
      </header>
      <div class="elevation-profile-sampling">
        <label class="elevation-spacing" title="Intervalo máximo entre lecturas: cerca de vértices próximos entre sí los pasos pueden ser menores.">Cotas cada
          <select data-profile-spacing aria-label="Intervalo entre cotas del perfil">${PROFILE_SPACING_OPTIONS.map((value) => `<option value="${value}">${value} m${value === PROFILE_SPACING_M ? ' · predeterminado' : ''}</option>`).join('')}<option value="custom">Otra distancia…</option></select>
        </label>
        <label class="elevation-spacing-custom" data-profile-spacing-custom-row hidden><input type="number" inputmode="decimal" min="${MIN_PROFILE_SPACING_M}" max="${MAX_PROFILE_SPACING_M}" step="any" data-profile-spacing-custom aria-label="Distancia personalizada entre cotas (m)" /> m</label>
        <span class="elevation-sampling-info" data-profile-sampling></span>
        <small class="elevation-sampling-note" data-profile-sampling-note hidden>Cada 10–20 m solo se interpolan más puntos del mismo DEM de ~30 m: no hay más resolución ni exactitud topográfica.</small>
      </div>
      <dl class="elevation-profile-stats" data-profile-stats></dl>
      <div class="elevation-profile-chart" data-profile-chart></div>
      <p class="elevation-profile-message" data-profile-message role="status"></p>
      <p class="elevation-disclaimer" title="${esc(HYDRAULIC_CAVEAT)}">${ELEVATION_DISCLAIMER}</p>`;
    this.chart?.dispose();
    this.chart = new ElevationProfileChart(this.element.querySelector<HTMLElement>('[data-profile-chart]')!, (index) => {
      const point = index !== null ? this.profile?.points[index] : undefined;
      this.overlay.setHover(point ? point.coordinate : null);
    });
    this.renderStats(null);
    this.syncSpacingControl();
    this.syncSelectedButton(this.store.getState());
  }

  private onSpacingPreset(value: string): void {
    const customRow = this.element.querySelector<HTMLElement>('[data-profile-spacing-custom-row]');
    if (value === 'custom') {
      if (customRow) customRow.hidden = false;
      const input = this.element.querySelector<HTMLInputElement>('[data-profile-spacing-custom]');
      if (input) { input.value = String(this.spacing); input.focus(); }
      return;
    }
    this.setSamplingInterval(Number(value));
  }

  private onSpacingCustom(value: string): void {
    const input = this.element.querySelector<HTMLInputElement>('[data-profile-spacing-custom]');
    const spacing = parseProfileSpacing(value);
    input?.setAttribute('aria-invalid', String(spacing === null));
    if (spacing === null) { this.setMessage(`Distancia no válida: escribe un valor entre ${MIN_PROFILE_SPACING_M} y ${MAX_PROFILE_SPACING_M.toLocaleString('es-NI')} m.`); return; }
    this.setSamplingInterval(spacing);
  }

  /** Reflects the current interval in the select (a non-preset value shows «Otra distancia» with its input). */
  private syncSpacingControl(): void {
    const select = this.element.querySelector<HTMLSelectElement>('[data-profile-spacing]'); if (!select) return;
    const preset = (PROFILE_SPACING_OPTIONS as readonly number[]).includes(this.spacing);
    select.value = preset ? String(this.spacing) : 'custom';
    const customRow = this.element.querySelector<HTMLElement>('[data-profile-spacing-custom-row]');
    const input = this.element.querySelector<HTMLInputElement>('[data-profile-spacing-custom]');
    if (customRow) customRow.hidden = preset;
    if (input && !preset) { input.value = String(this.spacing); input.setAttribute('aria-invalid', 'false'); }
    const note = this.element.querySelector<HTMLElement>('[data-profile-sampling-note]'); if (note) note.hidden = this.spacing >= PROFILE_SPACING_M;
    this.renderSampling();
  }

  /** «≤ 30 m · real máx. 29.8 m · 166 lecturas»: the interval actually used and the number of DEM readings. */
  private renderSampling(): void {
    const info = this.element.querySelector<HTMLElement>('[data-profile-sampling]'); if (!info) return;
    const route = this.route;
    info.textContent = route && route.samples.length > 1 && route.requestedSpacing === this.spacing
      ? `Paso real máx. ${metresLabel(route.spacing)} · ${route.samples.length.toLocaleString('es-NI')} lecturas${route.limited ? ' · limitado' : ''}`
      : '';
    info.classList.toggle('limited', Boolean(route?.limited));
  }

  private renderStats(profile: ElevationProfile | null): void {
    const host = this.element.querySelector<HTMLElement>('[data-profile-stats]'); if (!host) return;
    const name = this.element.querySelector<HTMLElement>('[data-profile-name]');
    if (name) name.textContent = this.source?.kind === 'planning' ? `${this.source.name} · solo lectura` : this.source ? 'Línea temporal' : '';
    this.element.classList.toggle('stale', this.stale);
    if (!profile) { host.innerHTML = ''; this.chart?.render(null); return; }
    const { stats } = profile;
    const item = (label: string, value: string, title = ''): string => `<div${title ? ` title="${esc(title)}"` : ''}><dt>${label}</dt><dd>${value}</dd></div>`;
    host.innerHTML = [
      item('Longitud', formatDistance(stats.length)),
      item('Inicio (A)', fmt(stats.start)),
      item('Fin (B)', fmt(stats.end)),
      item('Mínima', fmt(stats.min?.elevation)),
      item('Máxima', fmt(stats.max?.elevation)),
      item('Diferencia B − A', fmt(stats.difference, true), HYDRAULIC_CAVEAT),
      item('Pendiente media · preliminar', stats.meanSlopePercent === null ? 'Sin dato' : `${stats.meanSlopePercent > 0 ? '+' : stats.meanSlopePercent < 0 ? '−' : ''}${Math.abs(stats.meanSlopePercent).toLocaleString('es-NI', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} %`, 'Diferencia entre extremos dividida por la longitud. No describe el terreno intermedio.'),
    ].join('');
  }

  private setMessage(text: string): void {
    const message = this.element.querySelector<HTMLElement>('[data-profile-message]'); if (message) { message.textContent = text; message.hidden = !text; }
  }

  private syncSelectedButton(state: AppState): void {
    const button = this.element.querySelector<HTMLButtonElement>('[data-profile-action="selected"]'); if (button) button.disabled = !selectedPlanningLine(state);
  }

  private syncButtons(): void {
    document.querySelectorAll<HTMLElement>('.tool-button[data-elevation-tool="profile"]').forEach((button) => { button.classList.toggle('active', this.open); button.setAttribute('aria-pressed', String(this.open)); });
  }

  /** Stops an in-progress line capture (keeps the panel and any computed profile). */
  cancelDrawing(): void {
    if (!this.drawing) return;
    this.drawing = false;
    this.cancelling = true;
    try { this.drawingController.cancelSketch(); } finally { this.cancelling = false; }
    this.restoreTool();
  }

  private restoreTool(): void {
    const previous = this.previousTool; this.previousTool = null;
    if (previous && this.store.getState().activeTool === 'navigate') this.store.update({ activeTool: previous }, { markDirty: false });
  }

  private detachGeometry(): void { if (this.geometryListener) { unByKey(this.geometryListener); this.geometryListener = null; } }
}
